import { generateSortableID } from '@caresend/utils';
import Croppie from 'croppie';
import cloneDeep from 'lodash.clonedeep';
import { defineComponent, reactive, watch, computed, onMounted, ref, toRefs, nextTick } from 'vue';
import __vue_component__ from '../../alerts/Alert.vue.js';
import __vue_component__$1 from './subComponents/UploadedFileCard.vue.js';
import TransitionExpand from '../../transitions/TransitionExpand/TransitionExpand.js';
import { decryptFile } from '../../../database/encryption/decrypt.js';
import 'js-sha256';
import '../../../database/firebaseConfig/cloudFunctions.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import { DownloadOption } from '../../../database/encryption/model.js';
import 'firebase/auth';
import '../../../database/firebaseConfig/init.js';
import '../../../database/firebaseConfig/methods.js';
import '../../ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import { filePickerValueToUploadedFiles, initialValueIsArray, isEncryptedFile, urlToUploadedFile } from '../../../functions/useFilePickerValue.js';
import '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import { uploadFile } from '../../../database/firebaseConfig/storage.js';
import 'croppie/croppie.css';

const componentDefinition = defineComponent({
    name: 'FilePicker',
    components: {
        Alert: __vue_component__,
        TransitionExpand,
        UploadedFileCard: __vue_component__$1,
    },
    props: {
        accessAttributes: {
            type: Array,
            default: undefined,
        },
        additionalFilePlaceholder: {
            type: String,
            default: 'Drag or upload another file',
        },
        customOptions: {
            type: Array,
            default: () => [],
        },
        compress: {
            type: Boolean,
            default: true,
        },
        confirmDelete: Boolean,
        desktopOptions: {
            type: Array,
            default: () => [DownloadOption.OPEN, DownloadOption.DOWNLOAD],
        },
        encrypt: Boolean,
        fileTypes: {
            type: String,
            default: 'application/pdf,image/*',
        },
        fileNameOverride: {
            type: String,
            default: undefined,
        },
        highlight: {
            type: Boolean,
            default: false,
        },
        // Incoming `props.initialValue` and emitted events use `string` for unencrypted
        // file URLs and `EncryptedFile` objects for encrypted files.
        initialValue: {
            type: [String, Object, Array],
            default: null,
        },
        isMinimalView: Boolean,
        minimalViewStyle: {
            type: String,
            default: 'kebab',
        },
        multiple: Boolean,
        /**
         * By default, we only show the `download` button on devices detected as
         * mobile based on user agent.
         */
        mobileOptions: {
            type: Array,
            default: () => [DownloadOption.DOWNLOAD],
        },
        noDelete: Boolean,
        path: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
            default: 'Drag or upload a file',
        },
        size: {
            type: String,
            default: 'sm',
        },
        useOriginalFileName: Boolean,
        /** Only supports single file uploads at this stage */
        enableCropping: {
            type: Boolean,
            default: false,
        },
        /**
         * Instructions to display inside modal when cropping is enabled.
         */
        croppingInstructions: {
            type: String,
            default: undefined,
        },
        isReadOnly: Boolean,
    },
    setup(props, { emit }) {
        /** Encryption type guard for one `state.files` file */
        const isEncryptedUploadedFile = (file) => !!file.encryptedFile;
        /** Encryption type guard for multiple `state.files` file */
        const isEncryptedUploadedFiles = (files) => !!files[0] && isEncryptedUploadedFile(files[0]);
        const state = reactive({
            prevDropFiles: null,
            dropFiles: null,
            isDraggedOver: false,
            progress: 0,
            uploading: false,
            uploadingPDF: false,
            uploadPreviewURL: null,
            files: [],
            fileName: undefined,
        });
        const emitUploadedFiles = () => {
            emit('uploadedFilesUpdated', state.files);
        };
        watch(() => filePickerValueToUploadedFiles(props.initialValue, props.multiple), (newValue) => {
            state.files = newValue;
            /**
           * uploadedFilesUpdated is used for if the parent component needs the data
           * right away. That is why we call it on mount
           */
            emitUploadedFiles();
        }, { immediate: true });
        const classNames = computed(() => {
            const names = [];
            switch (props.size) {
                case 'sm':
                    names.push('FilePicker--sm');
                    break;
                case 'md':
                    names.push('FilePicker--md');
                    break;
                case 'lg':
                    names.push('FilePicker--lg');
                    break;
            }
            if (state.isDraggedOver)
                names.push('FilePicker--is-dragged-over');
            if (state.uploading)
                names.push('FilePicker--is-uploading');
            if (props.highlight)
                names.push('FilePicker--highlight');
            return names;
        });
        const showUploader = computed(() => props.path
            && (props.multiple || state.files.length === 0)
            && !props.isReadOnly);
        /** Takes an undecrypted `EncryptedUploadedFile` object, decrypts if
         * necessary, and returns the same object with a decrypted download URL
         * added. */
        const getDecryptedUploadedFile = async (uploadedFile) => {
            const { encryptedFile, isPDF } = uploadedFile;
            if (!encryptedFile)
                throw new Error('Encrypted File not found.');
            /** No need to decrypt PDF as there is no preview. */
            if (isPDF)
                return uploadedFile;
            const file = await decryptFile(encryptedFile, false);
            const url = URL.createObjectURL(file);
            return {
                ...uploadedFile,
                url,
            };
        };
        /** For a single file uploader */
        const setFile = (url, markAsPDF, encryptedFile) => {
            state.files = [urlToUploadedFile(url, markAsPDF, encryptedFile)];
        };
        /** For a multiple file uploader */
        const addFile = (url, markAsPDF, encryptedFile) => {
            state.files.push(urlToUploadedFile(url, markAsPDF, encryptedFile));
        };
        /** Update files that have been decrypted on mount without removing files
         * that were uploaded in the meantime, or re-adding files that were
         * deleted in the meantime. */
        const replaceDecryptedFiles = (updatedFiles) => {
            state.files = state.files.map((file) => {
                const matchingFile = updatedFiles.find((updatedFile) => file.localID === updatedFile.localID);
                return matchingFile !== null && matchingFile !== void 0 ? matchingFile : file;
            });
        };
        onMounted(async () => {
            // Decrypt existing files if needed.
            const firstFile = state.files[0];
            if (!props.initialValue || !firstFile)
                return;
            if (props.multiple && initialValueIsArray(props.initialValue, props.multiple)) {
                if (isEncryptedUploadedFiles(state.files)) {
                    const decryptPromises = state.files.map(async (file) => getDecryptedUploadedFile(file));
                    replaceDecryptedFiles(await Promise.all(decryptPromises));
                    emitUploadedFiles();
                }
            }
            else if (isEncryptedUploadedFile(firstFile)) {
                replaceDecryptedFiles([await getDecryptedUploadedFile(firstFile)]);
                emitUploadedFiles();
            }
        });
        /** Converts the array of `UploadedFile` objects in `state.files` back to
         * the appropriate type for emitted events */
        const getEmitValue = () => {
            var _a, _b, _c, _d;
            if (props.multiple) {
                if (props.encrypt) {
                    return state.files.map((file) => {
                        const { encryptedFile } = file;
                        if (!encryptedFile)
                            throw new Error('Encrypted File not found.');
                        return encryptedFile;
                    });
                }
                return state.files.map((file) => {
                    const { url } = file;
                    if (!url)
                        throw new Error('URL not found');
                    return url;
                });
            }
            if (props.encrypt) {
                return (_b = (_a = state.files[0]) === null || _a === void 0 ? void 0 : _a.encryptedFile) !== null && _b !== void 0 ? _b : null;
            }
            return (_d = (_c = state.files[0]) === null || _c === void 0 ? void 0 : _c.url) !== null && _d !== void 0 ? _d : null;
        };
        const emitInput = () => {
            emit('input', getEmitValue());
        };
        const weightedUpdateProgress = (prevFilesProgress, currentFileProgress, weight) => {
            state.progress = Math.round(prevFilesProgress + (currentFileProgress * weight));
        };
        const isAlertOpened = ref(false);
        const croppieElement = ref(null);
        const isCroppieOpen = ref(false);
        const croppieInstance = ref(null);
        const openCroppieAlert = async (url) => {
            var _a;
            isAlertOpened.value = true;
            isCroppieOpen.value = true;
            /**
             * wait until the next DOM update cycle before we try to select the
             * croppie html element
             */
            await nextTick();
            if (!croppieElement.value) {
                throw new Error('Croppie element not found.');
            }
            if (!url) {
                throw new Error('openCroppieAlert(): URL not found');
            }
            if (!croppieInstance.value) {
                croppieInstance.value = new Croppie(croppieElement.value, {
                    viewport: {
                        width: 160,
                        height: 160,
                        type: 'square',
                    },
                    showZoomer: false,
                });
            }
            await ((_a = croppieInstance.value) === null || _a === void 0 ? void 0 : _a.bind({ url }));
        };
        const closeCroppieAlert = () => {
            var _a;
            isCroppieOpen.value = false;
            isAlertOpened.value = false;
            if (!croppieInstance) {
                throw new Error('Croppie instance not found.');
            }
            (_a = croppieInstance.value) === null || _a === void 0 ? void 0 : _a.destroy();
            croppieInstance.value = null;
            state.dropFiles = null;
            state.fileName = undefined;
            if (state.uploadPreviewURL) {
                /**
                 * The URL.revokeObjectURL(state.uploadPreviewURL) method is used to
                 * release an object URL that was previously created using
                 *  URL.createObjectURL. This is important for managing memory and
                 * resources efficiently.
                 */
                URL.revokeObjectURL(state.uploadPreviewURL);
                state.uploadPreviewURL = null;
            }
            state.prevDropFiles = null;
        };
        const getCroppedResult = async () => {
            var _a;
            if (!croppieInstance) {
                throw new Error('Croppie instance not found.');
            }
            const croppedResult = await ((_a = croppieInstance.value) === null || _a === void 0 ? void 0 : _a.result({
                format: 'png',
                quality: 1,
                size: 'viewport',
                type: 'blob',
            }));
            return croppedResult;
        };
        /** Uploads all files in serial, providing a user of a preview of each file
         * as it is uploaded. */
        const recursiveUploadFiles = async (filesToUpload, index = 0, prevFilesProgress = 0) => {
            if (!filesToUpload.length) {
                return;
            }
            const progressUpdateWeight = 1 / filesToUpload.length;
            state.uploading = true;
            const file = filesToUpload[index];
            if (!file) {
                throw new Error('File not found.');
            }
            const localURL = URL.createObjectURL(file);
            const isPDF = file.type === 'application/pdf';
            state.uploadingPDF = isPDF;
            state.uploadPreviewURL = localURL;
            if (!props.path) {
                throw Error('Missing path prop.');
            }
            const path = props.useOriginalFileName
                ? `${props.path}/${generateSortableID()}` : props.path;
            const uploadedFile = await uploadFile(file, path, (currentFileProgress) => {
                weightedUpdateProgress(prevFilesProgress, currentFileProgress, progressUpdateWeight);
            }, !props.compress, props.encrypt, props.accessAttributes, props.useOriginalFileName);
            const newURL = !props.encrypt && !isEncryptedFile(uploadedFile)
                ? uploadedFile
                : localURL;
            const encryptedFile = isEncryptedFile(uploadedFile) ? uploadedFile : undefined;
            if (props.multiple) {
                addFile(newURL, isPDF, encryptedFile);
            }
            else {
                setFile(newURL, isPDF, encryptedFile);
            }
            emitInput();
            emitUploadedFiles();
            state.uploadingPDF = false;
            state.uploadPreviewURL = null;
            if (index === filesToUpload.length - 1) {
                state.uploading = false;
                state.progress = 0;
            }
            else {
                recursiveUploadFiles(filesToUpload, index + 1, state.progress);
            }
        };
        const saveFiles = async (files) => {
            var _a;
            if (!files)
                throw new Error('saveFiles: Files is null');
            let filesToUpload;
            if (Array.isArray(files)) {
                const { length } = files;
                const prevDropFilesAsArray = (_a = state.prevDropFiles) !== null && _a !== void 0 ? _a : [];
                if (!Array.isArray(prevDropFilesAsArray)) {
                    throw new Error('Expected previous dropFiles to be an array.');
                }
                const prevLength = prevDropFilesAsArray.length;
                filesToUpload = files.slice(prevLength, length);
            }
            else {
                filesToUpload = [files];
            }
            state.prevDropFiles = cloneDeep(files);
            await recursiveUploadFiles(filesToUpload);
        };
        const confirmCrop = async () => {
            var _a;
            const croppedResult = await getCroppedResult();
            if (!croppedResult)
                throw new Error('Cropped result not found');
            const file = new File([croppedResult], (_a = state.fileName) !== null && _a !== void 0 ? _a : 'file', { type: croppedResult.type });
            closeCroppieAlert();
            await saveFiles([file]);
        };
        // Watching for new file uploads
        const inputHandler = async (files) => {
            state.dropFiles = files;
            if (props.enableCropping) {
                if (Array.isArray(files)) {
                    throw new Error('inputHandler(): Expected a single file, but received an array.');
                }
                if (!files) {
                    throw new Error('inputHandler(): files is null');
                }
                /** input is just one File */
                const file = files;
                state.fileName = file.name;
                const localURL = URL.createObjectURL(file);
                await openCroppieAlert(localURL);
            }
            else {
                await saveFiles(files);
            }
        };
        const placeholderToDisplay = computed(() => {
            var _a;
            // If additional placeholder prop explicitly set to null, always show
            // placeholder prop.
            const additionalPlaceHolderToDisplay = (_a = props.additionalFilePlaceholder) !== null && _a !== void 0 ? _a : props.placeholder;
            return state.files.length
                ? additionalPlaceHolderToDisplay
                : props.placeholder;
        });
        const handleDelete = (index) => {
            state.files.splice(index, 1);
            state.dropFiles = null;
            state.prevDropFiles = null;
            if (state.uploadPreviewURL) {
                URL.revokeObjectURL(state.uploadPreviewURL);
                state.uploadPreviewURL = null;
            }
            emitInput();
            emitUploadedFiles();
        };
        const handleDragOn = () => {
            if (!state.isDraggedOver)
                state.isDraggedOver = true;
        };
        const handleDragOff = () => {
            if (state.isDraggedOver)
                state.isDraggedOver = false;
        };
        const setUploadingPDF = (value) => { state.uploadingPDF = value; };
        return {
            ...toRefs(state),
            classNames,
            closeCroppieAlert,
            confirmCrop,
            croppieElement,
            croppieInstance,
            getCroppedResult,
            handleDelete,
            handleDragOff,
            handleDragOn,
            inputHandler,
            isAlertOpened,
            isCroppieOpen,
            openCroppieAlert,
            placeholderToDisplay,
            setUploadingPDF,
            showUploader,
        };
    },
});

export { componentDefinition };
